import {useMemo, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {Link} from "react-router-dom";
import {AlertTriangle, ArrowUpDown, Building2, Eye, EyeOff, FileCheck2, GitBranch, MapPin, Plus, Tags, Users} from "lucide-react";
import {AdminTable} from "../../shared/components/AdminTable";
import {ActionChecklist, OperationsFlow} from "../../shared/components/BusinessBlocks";
import {DataState} from "../../shared/components/DataState";
import {PageSizeSelect} from "../../shared/components/PageSizeSelect";
import {PageToolbar, ToolbarButton, ToolbarInput, ToolbarSelect} from "../../shared/components/PageToolbar";
import {StatusBadge} from "../../shared/components/StatusBadge";
import {ServerPagination} from "../../shared/components/ServerPagination";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {ADMIN_ROLES} from "../../shared/auth/roles";
import {useTranslation} from "../../shared/i18n";
import {asList, formatNumber, getCount} from "../../shared/utils/format";
import {DEFAULT_PLACES_PAGE_SIZE, loadPlaceOperationsSummary, loadPlaceProperties, loadPlaces} from "./api";
import {OrganizationEditorPage} from "./OrganizationEditorPage";
import {PropertyCreationDialog} from "./components/PropertyCreationDialog";
import {useActiveAdminCountryScope} from "./useActiveAdminCountryScope";
import "./organizations.css";

const getStatusTone = (value) => (value ? "success" : "neutral");

const PAGE_SIZE_OPTIONS = [20, 50, 100];

const pageFromLink = (link) => {
    if (!link) return null;
    try {
        const parsed = new URL(link, "https://organizations.invalid");
        return Number(parsed.searchParams.get("page") || 1);
    } catch (_error) {
        return null;
    }
};

const PlatformOrganizationsPage = () => {
    const {t} = useTranslation();
    const capabilities = useCapabilities();
    const {countryScope, countryId} = useActiveAdminCountryScope(capabilities);
    const [draft, setDraft] = useState({search: "", city_id: "", category_id: ""});
    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PLACES_PAGE_SIZE);
    const [propertyDialog, setPropertyDialog] = useState(null);

    const placesQuery = useQuery({
        queryKey: ["admin-places", countryScope, filters, page, pageSize],
        queryFn: () => loadPlaces({
            ...filters,
            page,
            ...(pageSize === DEFAULT_PLACES_PAGE_SIZE ? {} : {page_size: pageSize}),
        }),
    });

    const propertiesQuery = useQuery({
        queryKey: ["admin-place-properties", countryScope],
        queryFn: loadPlaceProperties,
        retry: 1,
    });
    const summaryQuery = useQuery({
        queryKey: ["admin-place-operations-summary", countryScope],
        queryFn: loadPlaceOperationsSummary,
        retry: 1,
    });

    const places = asList(placesQuery.data);
    const cityOptions = propertiesQuery.data?.cities || propertiesQuery.data?.filterCities || [];
    const categoryOptions = propertiesQuery.data?.sub_categories || propertiesQuery.data?.categories || propertiesQuery.data?.filterCategories || [];
    const operations = summaryQuery.data || {};

    const summary = useMemo(() => {
        const visible = places.filter((place) => place.is_shown).length;
        const branches = places.reduce((sum, place) => sum + Number(place.branch_count || 0), 0);
        const bonusOnly = places.filter((place) => place.bonus_only).length;

        return {visible, branches, bonusOnly};
    }, [places]);

    const applyFilters = () => {
        setPage(1);
        setFilters({
            search: draft.search.trim(),
            city_id: draft.city_id,
            category_id: draft.category_id,
        });
    };

    const clearFilters = () => {
        setPage(1);
        setDraft({search: "", city_id: "", category_id: ""});
        setFilters({});
    };

    const changePageSize = (nextPageSize) => {
        setPage(1);
        setPageSize(nextPageSize);
    };

    const columns = [
        {
            key: "name",
            label: t("organizations.list.columns.place"),
            render: (place) => (
                <div>
                    <Link to={`/employers/${place.id}`}>{place.name || `#${place.id}`}</Link>
                    <div className="admin-muted">ID {place.id}</div>
                </div>
            ),
        },
        {key: "city", label: t("organizations.list.columns.city"), render: (place) => place.city || "—"},
        {key: "category", label: t("organizations.list.columns.category"), render: (place) => place.category || "—"},
        {
            key: "branches",
            label: t("organizations.list.columns.branches"),
            render: (place) => (
                <span className="admin-inline-actions">
                    <GitBranch size={14} />
                    {formatNumber(place.branch_count)}
                </span>
            ),
        },
        {key: "off_percent", label: t("organizations.list.columns.commission"), render: (place) => place.off_percent || "—"},
        {
            key: "is_shown",
            label: t("organizations.list.columns.inApp"),
            render: (place) => (
                <StatusBadge tone={getStatusTone(place.is_shown)}>
                    {place.is_shown ? t("organizations.list.columns.shown") : t("organizations.list.columns.hidden")}
                </StatusBadge>
            ),
        },
        {
            key: "bonus_only",
            label: t("organizations.list.columns.bonusOnly"),
            render: (place) => <StatusBadge tone={place.bonus_only ? "warning" : "neutral"}>{place.bonus_only ? t("organizations.list.columns.yes") : t("organizations.list.columns.no")}</StatusBadge>,
        },
        {key: "created_by", label: t("organizations.list.columns.deployer"), render: (place) => place.created_by || "—"},
    ];

    const attentionCount =
        Number(operations.places_without_branches || 0) +
        Number(operations.places_missing_legal || 0) +
        Number(operations.places_missing_media || 0) +
        Number(operations.branches_without_gallery || 0) +
        Number(operations.branches_without_staff || 0);
    const readinessBase = Math.max(1, Number(operations.total_places || getCount(placesQuery.data) || 0) * 3 + Number(operations.total_branches || 0) * 2);
    const readinessScore = Math.max(0, Math.round(((readinessBase - attentionCount) / readinessBase) * 100));
    const canCreate = capabilities.hasAction("organization.manage")
        && [ADMIN_ROLES.SUPERADMIN, ADMIN_ROLES.ADMINISTRATOR, ADMIN_ROLES.DEPLOYER, ADMIN_ROLES.IMPLEMENTATION_HEAD]
            .includes(capabilities.user?.role);
    const canManageProperties = capabilities.user?.role === ADMIN_ROLES.SUPERADMIN
        && capabilities.hasAction("system.manage");
    const canOrder = capabilities.hasAction("organization.manage")
        && [ADMIN_ROLES.SUPERADMIN, ADMIN_ROLES.ADMINISTRATOR].includes(capabilities.user?.role);
    const previousPage = pageFromLink(placesQuery.data?.previous);
    const nextPage = pageFromLink(placesQuery.data?.next);

    return (
        <div className="admin-page">
            <header className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">{t("organizations.list.title")}</h1>
                    <p className="admin-page-copy">{t("organizations.list.copy")}</p>
                </div>
                {canCreate ? (
                    <Link className="admin-button admin-button-primary organization-create-button" to="/employers/add">
                        <Plus aria-hidden="true" size={15} /> {t("organizations.list.create")}
                    </Link>
                ) : null}
            </header>

            <section className="admin-ops-layout">
                <OperationsFlow
                    title={t("organizations.list.flow.title")}
                    copy={t("organizations.list.flow.copy")}
                    steps={[
                        {title: t("organizations.list.flow.company.title"), copy: t("organizations.list.flow.company.copy")},
                        {title: t("organizations.list.flow.branches.title"), copy: t("organizations.list.flow.branches.copy")},
                        {title: t("organizations.list.flow.operations.title"), copy: t("organizations.list.flow.operations.copy")},
                        {title: t("organizations.list.flow.money.title"), copy: t("organizations.list.flow.money.copy")},
                    ]}
                    facts={[
                        {label: t("organizations.list.flow.readiness"), value: `${readinessScore}%`},
                        {label: t("organizations.list.flow.attention"), value: formatNumber(attentionCount)},
                        {label: t("organizations.list.flow.branchesTotal"), value: formatNumber(operations.total_branches ?? summary.branches)},
                    ]}
                />
                <div className="admin-ops-body">
                    <section className="admin-grid cols-4">
                        <article className="admin-card">
                            <p className="admin-card-title"><Building2 size={14} /> {t("organizations.list.cards.total")}</p>
                            <p className="admin-card-value">{formatNumber(operations.total_places ?? getCount(placesQuery.data))}</p>
                            <p className="admin-card-note">{t("organizations.list.cards.totalNote")}</p>
                        </article>
                        <article className="admin-card">
                            <p className="admin-card-title"><Eye size={14} /> {t("organizations.list.cards.visible")}</p>
                            <p className="admin-card-value">{formatNumber(operations.visible_places ?? summary.visible)}</p>
                            <p className="admin-card-note">{t("organizations.list.cards.hidden", {count: formatNumber(operations.hidden_places ?? Math.max(0, places.length - summary.visible))})}</p>
                        </article>
                        <article className="admin-card">
                            <p className="admin-card-title"><GitBranch size={14} /> {t("organizations.list.cards.branches")}</p>
                            <p className="admin-card-value">{formatNumber(operations.total_branches ?? summary.branches)}</p>
                            <p className="admin-card-note">{t("organizations.list.cards.showcase", {count: formatNumber(operations.visible_branches ?? 0)})}</p>
                        </article>
                        <article className="admin-card">
                            <p className="admin-card-title"><EyeOff size={14} /> {t("organizations.list.cards.bonusOnly")}</p>
                            <p className="admin-card-value">{formatNumber(operations.bonus_only_places ?? summary.bonusOnly)}</p>
                            <p className="admin-card-note">{t("organizations.list.cards.bonusNote")}</p>
                        </article>
                    </section>

                    <section className="admin-grid cols-3">
                        <article className="admin-card admin-health-card">
                            <p className="admin-card-title"><FileCheck2 size={14} /> {t("organizations.list.cards.readiness")}</p>
                            <p className="admin-card-value">{readinessScore}%</p>
                            <div className="admin-progress-track">
                                <span style={{width: `${readinessScore}%`}} />
                            </div>
                            <p className="admin-card-note">{t("organizations.list.cards.readinessNote")}</p>
                        </article>
                        <article className="admin-card">
                            <p className="admin-card-title"><AlertTriangle size={14} /> {t("organizations.list.cards.attention")}</p>
                            <ActionChecklist
                                items={[
                                    t("organizations.list.attention.noBranches", {count: formatNumber(operations.places_without_branches || 0)}),
                                    t("organizations.list.attention.missingLegal", {count: formatNumber(operations.places_missing_legal || 0)}),
                                    t("organizations.list.attention.noGallery", {count: formatNumber(operations.branches_without_gallery || 0)}),
                                ]}
                            />
                        </article>
                        <article className="admin-card">
                            <p className="admin-card-title"><Users size={14} /> {t("organizations.list.cards.staffing")}</p>
                            <ActionChecklist
                                items={[
                                    t("organizations.list.attention.noStaff", {count: formatNumber(operations.branches_without_staff || 0)}),
                                    t("organizations.list.attention.withMotivation", {count: formatNumber(operations.branches_with_staff_motivation || 0)}),
                                    t("organizations.list.attention.missingMedia", {count: formatNumber(operations.places_missing_media || 0)}),
                                ]}
                            />
                        </article>
                    </section>
                </div>
            </section>

            <PageToolbar>
                <ToolbarInput
                    label={t("organizations.list.filters.search")}
                    placeholder={t("organizations.list.filters.searchPlaceholder")}
                    value={draft.search}
                    onChange={(event) => setDraft((value) => ({...value, search: event.target.value}))}
                />
                <ToolbarSelect
                    label={t("organizations.list.filters.city")}
                    value={draft.city_id}
                    onChange={(event) => setDraft((value) => ({...value, city_id: event.target.value}))}
                >
                    <option value="">{t("organizations.list.filters.allCities")}</option>
                    {cityOptions.map((city) => (
                        <option key={city.id} value={city.id}>
                            {city.name}
                        </option>
                    ))}
                </ToolbarSelect>
                <ToolbarSelect
                    label={t("organizations.list.filters.category")}
                    value={draft.category_id}
                    onChange={(event) => setDraft((value) => ({...value, category_id: event.target.value}))}
                >
                    <option value="">{t("organizations.list.filters.allCategories")}</option>
                    {categoryOptions.map((category) => (
                        <option key={category.id} value={category.id}>
                            {category.name}
                        </option>
                    ))}
                </ToolbarSelect>
                <PageSizeSelect value={pageSize} onChange={changePageSize} options={PAGE_SIZE_OPTIONS} />
                <ToolbarButton onClick={applyFilters}>{t("organizations.list.filters.apply")}</ToolbarButton>
                <ToolbarButton variant="secondary" onClick={clearFilters}>
                    {t("organizations.list.filters.reset")}
                </ToolbarButton>
                {canManageProperties ? (
                    <>
                        <ToolbarButton variant="secondary" onClick={() => setPropertyDialog("city")}>
                            <MapPin aria-hidden="true" size={14} /> {t("organizations.list.filters.addCity")}
                        </ToolbarButton>
                        <ToolbarButton variant="secondary" onClick={() => setPropertyDialog("category")}>
                            <Tags aria-hidden="true" size={14} /> {t("organizations.list.filters.addCategory")}
                        </ToolbarButton>
                    </>
                ) : null}
                {canOrder && countryId ? (
                    <Link className="admin-button admin-button-secondary" to="/employers/change_positions">
                        <ArrowUpDown aria-hidden="true" size={14} /> {t("organizations.list.filters.reorder")}
                    </Link>
                ) : canOrder ? (
                    <span className="admin-muted" role="status">
                        {t("organizations.list.filters.pickCountry")}
                    </span>
                ) : null}
            </PageToolbar>

            {placesQuery.isError ? (
                <DataState
                    title={t("organizations.list.error.title")}
                    copy={t("organizations.list.error.copy")}
                />
            ) : !placesQuery.isLoading && getCount(placesQuery.data) === 0 && !Object.values(filters).some(Boolean) ? (
                <article className="organization-empty-state" aria-labelledby="organization-empty-heading">
                    <span className="organization-empty-icon">
                        <Building2 aria-hidden="true" size={20} />
                    </span>
                    <div>
                        <h2 id="organization-empty-heading">{t("organizations.list.empty.title")}</h2>
                        <p>{t("organizations.list.empty.copy")}</p>
                    </div>
                    {canCreate ? (
                        <Link className="admin-button admin-button-primary organization-create-button" to="/employers/add">
                            <Plus aria-hidden="true" size={15} /> {t("organizations.list.createFirst")}
                        </Link>
                    ) : null}
                </article>
            ) : (
                <AdminTable columns={columns} rows={places} loading={placesQuery.isLoading} emptyText={t("organizations.list.empty.table")} />
            )}
            {!placesQuery.isLoading && !placesQuery.isError && getCount(placesQuery.data) > 0 ? (
                <div className="organization-pagination">
                    <button
                        type="button"
                        className="admin-button admin-button-secondary"
                        disabled={placesQuery.isLoading || page === 1}
                        onClick={() => setPage(1)}
                    >
                        {t("organizations.list.pagination.first")}
                    </button>
                    <ServerPagination
                        count={getCount(placesQuery.data)}
                        page={page}
                        pageSize={pageSize}
                        previous={previousPage}
                        next={nextPage}
                        pending={placesQuery.isLoading}
                        onPageChange={setPage}
                    />
                </div>
            ) : null}
            <PropertyCreationDialog
                open={propertyDialog !== null}
                kind={propertyDialog}
                countryScope={countryScope}
                onCancel={() => setPropertyDialog(null)}
            />
        </div>
    );
};

export const OrganizationsPage = () => {
    const capabilities = useCapabilities();
    if (capabilities.user?.role === ADMIN_ROLES.OWNER) {
        return <OrganizationEditorPage mode="place" ownerWorkspace />;
    }
    return <PlatformOrganizationsPage />;
};
